import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import axios from 'axios';

// Configure axios to include credentials globally
axios.defaults.withCredentials = true;
import './App.css';

// Import API client pour apps natives
import { apiClient, getAuthData, clearAuthData, IS_NATIVE, API_URL } from './utils/http';

// Import notifications push natives
import { initNativePushNotifications, setNavigationCallback, setToastCallback, setAppReady, clearIOSBadge, retryPendingTokenRegistration } from './utils/nativePushNotifications';

// Error Boundary pour éviter les crashs sur Android
import AndroidErrorBoundary from './components/AndroidErrorBoundary';

// SEO Component pour les métadonnées dynamiques
const SEOHead = ({ title, description, path = '' }) => {
  const baseUrl = 'https://carewashgo.app';
  const fullTitle = title ? `${title} | CareWash GO` : 'CareWash GO | Application Gamifiée de Lavage Auto';
  const fullDesc = description || "L'application de lavage automobile gamifiée. Gagnez des points, débloquez des badges et défiez vos amis !";
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDesc} />
      <link rel="canonical" href={`${baseUrl}${path}`} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDesc} />
      <meta property="og:url" content={`${baseUrl}${path}`} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDesc} />
    </Helmet>
  );
};

// Components chargés immédiatement (critiques)
import Navigation from './components/Navigation';
import ConnectionStatus from './components/ConnectionStatus';
import LoadingSpinner from './components/LoadingSpinner';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import { ToastProvider } from './contexts/ToastContext';

// OPTIMISATION: Préchargement intelligent
import { prefetchEssentials, prefetchSecondary } from './utils/prefetch';

// OPTIMISATION: Lazy loading des composants lourds
const LoginPage = lazy(() => import('./components/LoginPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const Profile = lazy(() => import('./components/Profile'));
const AddWash = lazy(() => import('./components/AddWash'));
const LevelsDemo = lazy(() => import('./components/LevelsDemo'));
const ReviewsPage = lazy(() => import('./components/ReviewsPage'));

// Utiliser l'API URL
const API = IS_NATIVE ? API_URL + '/api' : '/api';
const AdminPage = lazy(() => import('./components/AdminPage'));
const Footer = lazy(() => import('./components/Footer'));
const PhotoGallery = lazy(() => import('./components/PhotoGallery'));
const ViralFeaturesPage = lazy(() => import('./components/ViralFeaturesPage'));
const BadgeCollection = lazy(() => import('./components/BadgeCollection'));
const SnakeGame = lazy(() => import('./components/SnakeGame'));
const EventsPage = lazy(() => import('./components/EventsPage'));
const Marketplace = lazy(() => import('./components/Marketplace'));
const ChallengesHub = lazy(() => import('./components/ChallengesHub'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const FAQ = lazy(() => import('./components/FAQ'));
const AvatarSelector = lazy(() => import('./components/AvatarSelector'));
const NotificationSettings = lazy(() => import('./components/NotificationSettings'));
const StatsDashboard = lazy(() => import('./components/StatsDashboard'));
const FriendsComparison = lazy(() => import('./components/FriendsComparison'));
const FriendsPage = lazy(() => import('./components/FriendsPage'));
const ChatPage = lazy(() => import('./components/ChatPage'));
const CarmagotchiPage = lazy(() => import('./components/CarmagotchiPage'));
const ReferralPage = lazy(() => import('./components/ReferralPage'));
const GoalsPage = lazy(() => import('./components/GoalsPage'));
const TrophiesPage = lazy(() => import('./components/TrophiesPage'));
const GamesHub = lazy(() => import('./components/GamesHub'));
const SpinWheel = lazy(() => import('./components/SpinWheel'));
const QuickTap = lazy(() => import('./components/QuickTap'));
const Connect4Page = lazy(() => import('./components/Connect4Page'));
// Tombola pages
const TombolaRulesPage = lazy(() => import('./components/TombolaRulesPage'));
const TombolaParticipantsPage = lazy(() => import('./components/TombolaParticipantsPage'));
const TombolaHistoryPage = lazy(() => import('./components/TombolaHistoryPage'));
// 🥚 Creatures & Eggs - Collection de créatures
const CreaturesPage = lazy(() => import('./components/CreaturesPage'));
// 📊 Affluence - Heures creuses
const AffluencePage = lazy(() => import('./components/AffluencePage'));
// Treasure Hunt - Chasse au trésor géolocalisée (OPTIMISÉ: lazy loading)
const TreasureHuntPage = lazy(() => import('./components/TreasureHunt/TreasureHuntPage'));
const TreasureAdmin = lazy(() => import('./components/TreasureHunt/TreasureAdmin'));
const AdminClaims = lazy(() => import('./components/TreasureHunt/AdminClaims'));
const MesGains = lazy(() => import('./components/TreasureHunt/MesGains'));
// Partner pages (OPTIMISÉ: lazy loading)
const PartnerPage = lazy(() => import('./components/PartnerPage'));
const PartnersNearby = lazy(() => import('./components/PartnersNearby'));
const AdminPartnersDirectory = lazy(() => import('./components/AdminPartnersDirectory'));
const AdminPartners = lazy(() => import('./components/AdminPartners'));
// On-Demand (CareWash x CarGlow)
const CarGlowBooking = lazy(() => import('./components/CarGlowBooking'));
const CarGlowMyBookings = lazy(() => import('./components/CarGlowMyBookings'));
const MyNayaxCards = lazy(() => import('./components/MyNayaxCards'));
const NayaxCardDetail = lazy(() => import('./components/NayaxCardDetail'));
// Station problem reports
const ReportProblem = lazy(() => import('./components/ReportProblem'));
const MyReports = lazy(() => import('./components/MyReports'));
const StationReportsAdmin = lazy(() => import('./components/StationReportsAdmin'));
// WashStock
const WashStock = lazy(() => import('./components/WashStock'));
const WashStockLeaderboard = lazy(() => import('./components/WashStockLeaderboard'));
const WashStockHistory = lazy(() => import('./components/WashStockHistory'));
const WashStockAdmin = lazy(() => import('./components/WashStockAdmin'));
const WashStockPredictions = lazy(() => import('./components/WashStockPredictions'));
const WashStockLeague = lazy(() => import('./components/WashStockLeague'));
const WashStockPublicProfile = lazy(() => import('./components/WashStockPublicProfile'));
// MY CAR - Hub automobile
const MyCarHub = lazy(() => import('./components/MyCar/MyCarHub'));
const AddVehicle = lazy(() => import('./components/MyCar/AddVehicle'));
const ParkingPage = lazy(() => import('./components/MyCar/Parking/ParkingPage'));
const FuelPage = lazy(() => import('./components/MyCar/Fuel/FuelPage'));
const MaintenancePage = lazy(() => import('./components/MyCar/Maintenance/MaintenancePage'));
const ExpensesPage = lazy(() => import('./components/MyCar/Expenses/ExpensesPage'));
const RemindersPage = lazy(() => import('./components/MyCar/Reminders/RemindersPage'));
const FinancingPage = lazy(() => import('./components/MyCar/Financing/FinancingPage'));
// Pages légales
const LegalPage = lazy(() => import('./components/LegalPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Footer wrapper with auth check
const FooterWithAuth = () => {
  const { user } = useAuth();
  return user ? <Footer /> : null;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingSession, setProcessingSession] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [pushInitialized, setPushInitialized] = useState(false);
  const authCheckRef = React.useRef(false);

  // 📱 FIX: Clear iOS badge IMMEDIATELY on app start (before any login check)
  useEffect(() => {
    if (IS_NATIVE) {
      console.log('📱 Clearing iOS badge on app start...');
      clearIOSBadge();
    }
  }, []); // Empty deps = runs once on mount

  // 🔧 FIX: Initialize push notifications separately to avoid race conditions
  const initPushNotificationsOnce = async (userId) => {
    if (pushInitialized || !IS_NATIVE || !userId) return;
    
    setPushInitialized(true);
    try {
      await initNativePushNotifications(userId);
      console.log('📱 Push notifications initialized successfully');
    } catch (pushError) {
      console.warn('📱 Push notifications not available:', pushError);
    }
  };

  const checkAuth = async (isRefresh = false) => {
    // 🔧 FIX: Triple-check to prevent race conditions
    // Check 1: React state
    if (isLoggingIn) {
      console.log('🔐 Skipping checkAuth - login in progress (state)');
      setLoading(false);
      return;
    }
    
    // Check 2: localStorage flag (persists across re-renders)
    if (localStorage.getItem('isLoggingIn') === 'true') {
      console.log('🔐 Skipping checkAuth - login in progress (localStorage)');
      setLoading(false);
      return;
    }
    
    // Check 3: Ref to prevent concurrent calls
    if (authCheckRef.current) {
      console.log('🔐 Skipping checkAuth - already checking');
      return;
    }
    
    authCheckRef.current = true;
    
    try {
      // Pour l'app native, vérifier d'abord si on a un userId stocké
      if (IS_NATIVE) {
        const { userId } = await getAuthData();
        if (userId) {
          // Utiliser le nouvel endpoint native
          const response = await apiClient.get(`/api/users/native/${userId}`);
          if (response.data) {
            // 📱 FIX DÉFINITIF: Force sync notifications AVANT de set le user
            let userData = response.data;
            
            try {
              // 1. Clear iOS badge localement
              clearIOSBadge();
              
              // 2. Force sync sur le backend (marque tout comme lu + reset compteur)
              const syncRes = await apiClient.post(`/api/notifications/force-sync`);
              console.log('📱 Notifications synced:', syncRes.data);
              
              // 3. Forcer le compteur à 0 dans les données user AVANT de les mettre dans le state
              if (syncRes.data?.success) {
                userData = { ...userData, notifications_count: 0 };
              }
            } catch (e) {
              console.log('Notifications sync skipped:', e.message);
              // En cas d'erreur, on force quand même à 0 localement
              userData = { ...userData, notifications_count: 0 };
            }
            
            // Maintenant on set le user avec le compteur déjà corrigé
            setUser(userData);
            setLoading(false);
            
            // 📱 FIX: Store userId globally for native request fallback
            if (typeof window !== 'undefined') {
              window.__CAREWASH_USER_ID__ = userId;
            }
            
            // 🔧 FIX: Initialize push notifications AFTER state is set
            // Using requestAnimationFrame to ensure React has committed the state update
            requestAnimationFrame(() => {
              initPushNotificationsOnce(userId);
            });
            
            return;
          }
        }
        // Pas de userId stocké ou erreur
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Pour le web, utiliser les cookies
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 10000
      });
      
      // 📱 FIX DÉFINITIF WEB: Force sync notifications pour le web aussi
      let userData = response.data;
      try {
        const syncRes = await apiClient.post(`/api/notifications/force-sync`);
        if (syncRes.data?.success) {
          userData = { ...userData, notifications_count: 0 };
        }
      } catch (e) {
        // En cas d'erreur, forcer à 0 localement
        userData = { ...userData, notifications_count: 0 };
      }
      
      setUser(userData);
      
      // 📱 FIX: Store userId globally for fallback
      if (typeof window !== 'undefined' && userData?.id) {
        window.__CAREWASH_USER_ID__ = userData.id;
      }
      
      // 📱 PUSH: Réessayer d'enregistrer le token en attente après login
      if (IS_NATIVE && userData?.id) {
        console.log('📱 Attempting to retry pending push token registration after login...');
        retryPendingTokenRegistration(userData.id);
      }
      
      // Onboarding désactivé - les stores (App Store/Play Store) fournissent déjà les infos
      // Code conservé pour référence si besoin de réactiver
      // const isAdminPage = window.location.pathname === '/admin';
      // if (!response.data.has_seen_onboarding && !isAdminPage) {
      //   setTimeout(() => setShowOnboarding(true), 1000);
      // }
    } catch (error) {
      if (!isRefresh) {
        setUser(null);
      }
    } finally {
      setLoading(false);
      authCheckRef.current = false; // 🔧 FIX: Reset ref to allow future checks
      
      if (user) {
        prefetchSecondary();
      }
    }
  };

  const processSessionId = async (sessionId) => {
    // Cette fonction n'est utilisée que sur le web (Google Auth)
    if (IS_NATIVE) {
      return;
    }
    
    setProcessingSession(true);
    try {
      await axios.post(`${API}/auth/session`, {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });
      
      // Clean URL fragment
      window.history.replaceState(null, null, window.location.pathname);
      
      // Check auth again
      await checkAuth();
    } catch (error) {
      console.error('Session processing failed:', error);
    } finally {
      setProcessingSession(false);
    }
  };

  const logout = async () => {
    try {
      // Pour l'app native, supprimer les données locales
      if (IS_NATIVE) {
        await clearAuthData();
        setUser(null);
        return;
      }
      
      // Pour le web, utiliser le cookie
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // En cas d'erreur, forcer le logout quand même
      if (IS_NATIVE) {
        await clearAuthData();
      }
      setUser(null);
    }
  };

  useEffect(() => {
    // Configurer les callbacks pour les notifications push natives (Android-safe)
    if (IS_NATIVE) {
      // Le callback de navigation sera configuré dans le composant App qui a accès au router
    }
    
    // Check for session_id in URL fragment
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const sessionId = params.get('session_id');

    if (sessionId) {
      processSessionId(sessionId);
    } else {
      checkAuth();
    }
    
    // OPTIMISATION: Précharger les données essentielles
    prefetchEssentials();
  }, []);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    
    // Marquer l'onboarding comme complété sur le serveur
    try {
      if (IS_NATIVE) {
        await apiClient.post(`${API}/auth/onboarding-complete`, {});
      } else {
        await axios.post(`${API}/auth/onboarding-complete`, {}, {
          withCredentials: true
        });
      }
      
      // Mettre à jour l'état local de l'utilisateur
      if (user) {
        setUser({ ...user, has_seen_onboarding: true });
      }
    } catch (error) {
      console.error('Failed to mark onboarding as complete:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    setShowOnboarding(false);
    
    // Marquer l'onboarding comme complété même si skip
    try {
      if (IS_NATIVE) {
        await apiClient.post(`${API}/auth/onboarding-complete`, {});
      } else {
        await axios.post(`${API}/auth/onboarding-complete`, {}, {
          withCredentials: true
        });
      }
      
      // Mettre à jour l'état local de l'utilisateur
      if (user) {
        setUser({ ...user, has_seen_onboarding: true });
      }
    } catch (error) {
      console.error('Failed to mark onboarding as complete:', error);
    }
  };

  // Wrapper function for refresh that passes isRefresh=true
  const refreshAuth = async () => {
    return checkAuth(true);
  };

  // 🔔 FIX: Function to update user state locally (for notifications, etc.)
  const updateUser = (updates) => {
    if (user) {
      setUser(prev => ({ ...prev, ...updates }));
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      processingSession,
      logout,
      refreshAuth,
      processSessionId,
      setIsLoggingIn,
      updateUser
    }}>
      {children}
      
      {/* Onboarding Modal - Désactivé (les stores fournissent déjà les infos) */}
      {/* {showOnboarding && user && (
        <Onboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )} */}
    </AuthContext.Provider>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, processingSession } = useAuth();

  if (loading || processingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const CarGlowRoute = ({ children }) => {
  const { user, loading, processingSession } = useAuth();
  const [publicEnabled, setPublicEnabled] = useState(null);

  useEffect(() => {
    apiClient.get(`${API}/carglow/visibility`)
      .then(r => setPublicEnabled(!!r.data?.public_enabled))
      .catch(() => setPublicEnabled(false));
  }, []);

  if (loading || processingSession || publicEnabled === null) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!publicEnabled && !user.is_admin && !user.carglow_access) {
    return <Navigate to="/" replace />;
  }

  return children;
};


// Composant pour configurer les callbacks de navigation push (Android-safe)
const PushNavigationSetup = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (IS_NATIVE) {
      // Configurer le callback de navigation pour les notifications push
      setNavigationCallback((path) => {
        try {
          navigate(path);
        } catch (e) {
          console.warn('📱 Navigation callback error:', e);
        }
      });
      
      // Marquer l'app comme prête après un délai
      const readyTimer = setTimeout(() => {
        setAppReady(true);
      }, 3000);
      
      
      return () => {
        clearTimeout(readyTimer);
        if (IS_NATIVE) {
          setNavigationCallback(null);
          setAppReady(false);
        }
      };
    }
  }, [navigate]);
  
  return null;
};

// Main App component
function App() {
  return (
    <AndroidErrorBoundary>
    <HelmetProvider>
    <div className="App">
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <PushNavigationSetup />
            <ConnectionStatus />
            <OfflineIndicator />
            <NotificationPermissionPrompt />
            <PWAInstallPrompt />
            <Suspense fallback={
              <LoadingSpinner 
                message="⚡ Chargement de la page..." 
                size="large" 
                fullScreen={true}
              />
            }>
            <Routes>
            {/* Pages légales (publiques) */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ResetPasswordPage />} />
            
            <Route path="/" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <Dashboard />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/leaderboard" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <Leaderboard />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <Profile />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/avatar-selector" element={
              <ProtectedRoute>
                <Navigation />
                <AvatarSelector />
              </ProtectedRoute>
            } />
            <Route path="/notification-settings" element={
              <ProtectedRoute>
                <Navigation />
                <NotificationSettings />
              </ProtectedRoute>
            } />
            <Route path="/stats" element={
              <ProtectedRoute>
                <Navigation />
                <StatsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/friends" element={
              <ProtectedRoute>
                <Navigation />
                <FriendsPage />
              </ProtectedRoute>
            } />
            <Route path="/chat/:friendId" element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            } />
            <Route path="/friends-comparison" element={
              <ProtectedRoute>
                <Navigation />
                <FriendsComparison />
              </ProtectedRoute>
            } />
            <Route path="/referral" element={
              <ProtectedRoute>
                <Navigation />
                <ReferralPage />
              </ProtectedRoute>
            } />
            <Route path="/carmagotchi" element={
              <ProtectedRoute>
                <Navigation />
                <CarmagotchiPage />
              </ProtectedRoute>
            } />
            <Route path="/goals" element={
              <ProtectedRoute>
                <Navigation />
                <GoalsPage />
              </ProtectedRoute>
            } />
            <Route path="/trophies" element={
              <ProtectedRoute>
                <Navigation />
                <TrophiesPage />
              </ProtectedRoute>
            } />
            <Route path="/add-wash" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <AddWash />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/levels" element={<LevelsDemo />} />
            <Route path="/reviews" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <ReviewsPage />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/photos" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
                  <Navigation />
                  <PhotoGallery />
                </div>
              </ProtectedRoute>
            } />
            <Route path="/viral-features" element={
              <ProtectedRoute>
                <ViralFeaturesPage />
              </ProtectedRoute>
            } />
            <Route path="/badges" element={
              <ProtectedRoute>
                <BadgeCollection />
              </ProtectedRoute>
            } />
            <Route path="/challenges-hub" element={
              <ProtectedRoute>
                <ChallengesHub />
              </ProtectedRoute>
            } />
            <Route path="/challenges" element={
              <ProtectedRoute>
                <ChallengesHub />
              </ProtectedRoute>
            } />
            <Route path="/snake" element={
              <ProtectedRoute>
                <Navigation />
                <SnakeGame />
              </ProtectedRoute>
            } />
            <Route path="/games" element={
              <ProtectedRoute>
                <Navigation />
                <ChallengesHub />
              </ProtectedRoute>
            } />
            <Route path="/spin-wheel" element={
              <ProtectedRoute>
                <Navigation />
                <SpinWheel />
              </ProtectedRoute>
            } />
            <Route path="/quick-tap" element={
              <ProtectedRoute>
                <Navigation />
                <QuickTap />
              </ProtectedRoute>
            } />
            <Route path="/connect4" element={
              <ProtectedRoute>
                <Connect4Page />
              </ProtectedRoute>
            } />
            <Route path="/events" element={
              <ProtectedRoute>
                <EventsPage />
              </ProtectedRoute>
            } />
            <Route path="/marketplace" element={
              <ProtectedRoute>
                <Marketplace />
              </ProtectedRoute>
            } />
            <Route path="/faq" element={
              <ProtectedRoute>
                <div className="min-h-screen">
                  <Navigation />
                  <FAQ />
                </div>
              </ProtectedRoute>
            } />
            {/* Tombola Routes */}
            <Route path="/tombola/rules" element={
              <ProtectedRoute>
                <Navigation />
                <TombolaRulesPage />
              </ProtectedRoute>
            } />
            <Route path="/tombola/participants" element={
              <ProtectedRoute>
                <Navigation />
                <TombolaParticipantsPage />
              </ProtectedRoute>
            } />
            <Route path="/tombola/history" element={
              <ProtectedRoute>
                <Navigation />
                <TombolaHistoryPage />
              </ProtectedRoute>
            } />
            {/* 🥚 Creatures - Collection et éclosion d'œufs */}
            <Route path="/creatures" element={
              <ProtectedRoute>
                <CreaturesPage />
              </ProtectedRoute>
            } />
            <Route path="/collection" element={
              <ProtectedRoute>
                <CreaturesPage />
              </ProtectedRoute>
            } />
            {/* 📊 Affluence - Heures creuses */}
            <Route path="/affluence" element={
              <ProtectedRoute>
                <AffluencePage />
              </ProtectedRoute>
            } />
            {/* Treasure Hunt - Chasse au trésor */}
            <Route path="/treasure" element={
              <ProtectedRoute>
                <TreasureHuntPage />
              </ProtectedRoute>
            } />
            <Route path="/mes-gains" element={
              <ProtectedRoute>
                <MesGains />
              </ProtectedRoute>
            } />
            <Route path="/admin/treasure" element={
              <ProtectedRoute>
                <TreasureAdmin />
              </ProtectedRoute>
            } />
            <Route path="/admin/treasure/claims" element={
              <ProtectedRoute>
                <AdminClaims />
              </ProtectedRoute>
            } />
            <Route path="/partner" element={
              <ProtectedRoute>
                <PartnerPage />
              </ProtectedRoute>
            } />
            <Route path="/partenaires" element={
              <ProtectedRoute>
                <PartnersNearby />
              </ProtectedRoute>
            } />
            <Route path="/admin/partners-directory" element={
              <ProtectedRoute>
                <AdminPartnersDirectory />
              </ProtectedRoute>
            } />
            <Route path="/admin/partners" element={
              <ProtectedRoute>
                <AdminPartners />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
                  <Navigation />
                  <AdminPage />
                </div>
              </ProtectedRoute>
            } />
            {/* CareWash x CarGlow - Nettoyage à domicile */}
            <Route path="/ondemand" element={
              <CarGlowRoute>
                <CarGlowBooking />
              </CarGlowRoute>
            } />
            <Route path="/my-bookings" element={
              <CarGlowRoute>
                <CarGlowMyBookings />
              </CarGlowRoute>
            } />
            <Route path="/my-cards" element={
              <ProtectedRoute>
                <MyNayaxCards />
              </ProtectedRoute>
            } />
            <Route path="/my-cards/:linkId" element={
              <ProtectedRoute>
                <NayaxCardDetail />
              </ProtectedRoute>
            } />
            {/* Station problem reports */}
            <Route path="/report-problem" element={
              <ProtectedRoute>
                <ReportProblem />
              </ProtectedRoute>
            } />
            <Route path="/my-reports" element={
              <ProtectedRoute>
                <MyReports />
              </ProtectedRoute>
            } />
            <Route path="/admin/station-reports" element={
              <ProtectedRoute>
                <StationReportsAdmin />
              </ProtectedRoute>
            } />
            {/* WashStock */}
            <Route path="/washstock" element={
              <ProtectedRoute>
                <WashStock />
              </ProtectedRoute>
            } />
            <Route path="/washstock/leaderboard" element={
              <ProtectedRoute>
                <WashStockLeaderboard />
              </ProtectedRoute>
            } />
            <Route path="/washstock/history" element={
              <ProtectedRoute>
                <WashStockHistory />
              </ProtectedRoute>
            } />
            <Route path="/washstock/predictions" element={
              <ProtectedRoute>
                <WashStockPredictions />
              </ProtectedRoute>
            } />
            <Route path="/washstock/league" element={
              <ProtectedRoute>
                <WashStockLeague />
              </ProtectedRoute>
            } />
            <Route path="/p/:username" element={<WashStockPublicProfile />} />
            <Route path="/admin/washstock" element={
              <ProtectedRoute>
                <WashStockAdmin />
              </ProtectedRoute>
            } />
            {/* MY CAR - Hub automobile */}
            <Route path="/mycar" element={
              <ProtectedRoute>
                <Navigation />
                <MyCarHub />
              </ProtectedRoute>
            } />
            <Route path="/mycar/vehicle/new" element={
              <ProtectedRoute>
                <AddVehicle />
              </ProtectedRoute>
            } />
            <Route path="/mycar/vehicles" element={
              <ProtectedRoute>
                <Navigation />
                <MyCarHub />
              </ProtectedRoute>
            } />
            <Route path="/mycar/parking/:vehicleId" element={
              <ProtectedRoute>
                <ParkingPage />
              </ProtectedRoute>
            } />
            <Route path="/mycar/fuel/:vehicleId" element={
              <ProtectedRoute>
                <FuelPage />
              </ProtectedRoute>
            } />
            <Route path="/mycar/maintenance/:vehicleId" element={
              <ProtectedRoute>
                <MaintenancePage />
              </ProtectedRoute>
            } />
            <Route path="/mycar/expenses/:vehicleId" element={
              <ProtectedRoute>
                <ExpensesPage />
              </ProtectedRoute>
            } />
            <Route path="/mycar/reminders/:vehicleId" element={
              <ProtectedRoute>
                <RemindersPage />
              </ProtectedRoute>
            } />
            <Route path="/mycar/financing/:vehicleId" element={
              <ProtectedRoute>
                <FinancingPage />
              </ProtectedRoute>
            } />
            {/* 404 Page */}
            <Route path="*" element={
              <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-8">Page non trouvée</p>
                  <a href="/" className="btn-primary">Retour à l'accueil</a>
                </div>
              </div>
            } />
          </Routes>
          </Suspense>
        </BrowserRouter>
        
        {/* Footer avec mentions légales */}
        <FooterWithAuth />
        </ToastProvider>
      </AuthProvider>
    </div>
    </HelmetProvider>
    </AndroidErrorBoundary>
  );
}

export { SEOHead };
export default App;